/* ==========================================================================
   PWR CART
   Cart + wishlist layer. Loaded AFTER pwr-utils, pwr-events, pwr-core.
   Registers window.PWR.cart and window.PWR.wishlist.

   Cart strategy:
     Wraps Dawn's commerce engine — never reimplements it. add() POSTs to
     routes.cart_add_url, requests Dawn's cart section renders in the same
     round-trip, hands the returned HTML to Dawn's <cart-drawer> /
     <cart-notification> custom elements, then emits PWR.emitCartUpdated so
     both the PWR bus AND Dawn's pub/sub hear about it. Zero backend divergence.

   Wishlist strategy (honest constraint):
     With no app and no storefront write access to customer metafields, a
     wishlist can only persist client-side (localStorage) for guests. This is
     the durable client layer; syncToAccount() is a documented seam for a
     future app / customer-metafield backend. It is NOT account-synced today.

   Delegated hooks (work for any section, present or future):
     [data-pwr-quick-add][data-variant-id]        -> add to cart
     [data-pwr-wishlist-toggle][data-product-id]  -> toggle wishlist
   ========================================================================== */
(function () {
  'use strict';

  window.PWR = window.PWR || {};
  if (window.PWR.cart) return;

  const { utils, bus, EVENTS, emitCartUpdated } = window.PWR;

  /* ==========================================================================
     CART
     ========================================================================== */
  class Cart {
    constructor() {
      this._pending = new WeakSet();
    }

    /**
     * Section IDs Dawn should re-render alongside a cart mutation.
     * Deliberately excludes 'cart-icon-bubble': this theme's header icon/count
     * markup is custom (sections/pwr-header.liquid), not Dawn's stock bubble,
     * and is kept in sync independently via utils.syncCartCount() below.
     */
    _sectionsToRender() {
      const list = [];
      if (document.getElementById('cart-notification')) list.push('cart-notification');
      if (document.getElementById('cart-drawer')) list.push('cart-drawer');
      if (document.getElementById('cart-live-region-text')) list.push('cart-live-region-text');
      return list;
    }

    /**
     * Add a single variant to the cart.
     * @param {number|string} variantId
     * @param {object} [opts] { quantity, properties, sellingPlanId }
     * @returns {Promise<object>} Shopify add.js response
     */
    async add(variantId, opts) {
      const options = opts || {};
      const item = {
        id: Number(variantId),
        quantity: options.quantity || 1,
      };
      if (options.properties) item.properties = options.properties;
      if (options.sellingPlanId) item.selling_plan = options.sellingPlanId;

      const body = { items: [item] };
      const sections = this._sectionsToRender();
      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      bus.emit(EVENTS.cartAdd, { variantId: item.id });

      try {
        const data = await utils.fetchJSON(utils.route('cart_add_url'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        });

        this._renderDawnCart(data);
        utils.syncCartCount();
        emitCartUpdated({ source: 'pwr-cart', cartData: data, variantId: item.id });
        bus.emit(EVENTS.cartAdded, { variantId: item.id, cartData: data });
        utils.announce(window.PWR.strings?.addedToCart || 'Added to cart');
        return data;
      } catch (error) {
        const message = (error.data && (error.data.description || error.data.message))
          || (window.PWR.strings && window.PWR.strings.cartError)
          || 'Could not add to cart';
        bus.emit(EVENTS.cartError, { variantId: item.id, message, error });
        utils.announce(message, true);
        throw error;
      }
    }

    /** Fetch current cart state as JSON (no mutation). */
    async get() {
      return utils.fetchJSON(utils.route('cart_url') + '.js', {
        headers: { Accept: 'application/json' },
      });
    }

    /** Hand rendered section HTML to Dawn's cart custom elements. */
    _renderDawnCart(data) {
      if (!data || !data.sections) return;
      const drawer = document.querySelector('cart-drawer');
      const notification = document.querySelector('cart-notification');

      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
        if (typeof drawer.open === 'function') drawer.open();
        bus.emit(EVENTS.cartOpen, { via: 'drawer' });
        return;
      }
      if (notification && typeof notification.renderContents === 'function') {
        notification.renderContents(data);
      }
    }
  }

  /* ==========================================================================
     WISHLIST  (localStorage, guest-durable)
     ========================================================================== */
  const WISHLIST_KEY = 'pwr:wishlist';

  class Wishlist {
    constructor() {
      this._ids = new Set(utils.storage.get(WISHLIST_KEY, []));
    }

    ids() { return Array.from(this._ids); }
    count() { return this._ids.size; }
    has(productId) { return this._ids.has(String(productId)); }

    toggle(productId) {
      const id = String(productId);
      if (this._ids.has(id)) {
        this._ids.delete(id);
      } else {
        this._ids.add(id);
      }
      this._persist();
      return this.has(id);
    }

    add(productId) {
      this._ids.add(String(productId));
      this._persist();
    }

    remove(productId) {
      this._ids.delete(String(productId));
      this._persist();
    }

    clear() {
      this._ids.clear();
      this._persist();
    }

    _persist() {
      utils.storage.set(WISHLIST_KEY, this.ids());
      bus.emit(EVENTS.wishlistChanged, { ids: this.ids(), count: this.count() });
      this._reflect();
    }

    /** Sync all wishlist toggle buttons in the DOM to current state. */
    _reflect(scope) {
      utils.qsa('[data-pwr-wishlist-toggle]', scope || document).forEach((btn) => {
        const id = btn.getAttribute('data-product-id');
        const active = this.has(id);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      // Update any count badges.
      utils.qsa('[data-pwr-wishlist-count]').forEach((el) => {
        el.textContent = String(this.count());
        el.hidden = this.count() === 0;
      });
    }

    /**
     * Documented seam for a future backend (app block or customer metafield).
     * No-op today; wire to your endpoint when available.
     */
    syncToAccount(/* customerId */) {
      return Promise.resolve(false);
    }
  }

  /* ==========================================================================
     DELEGATED EVENT WIRING  (single listener each; works for future DOM)
     ========================================================================== */
  const cart = new Cart();
  const wishlist = new Wishlist();

  function setButtonLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    button.disabled = loading;
  }

  function flashSuccess(button) {
    if (!button) return;
    button.classList.add('is-success');
    const label = button.querySelector('.pwr-btn__label');
    const original = label ? label.textContent : '';
    if (label) label.textContent = window.PWR.strings?.added || 'Added \u2713';
    window.setTimeout(() => {
      button.classList.remove('is-success');
      if (label) label.textContent = original;
    }, 2000);
  }

  utils.ready(() => {
    // Quick add (single-variant fast path). Multi-variant CTAs are links.
    utils.on(document, 'click', '[data-pwr-quick-add]', async (event, button) => {
      if (button.classList.contains('is-loading')) return;
      event.preventDefault();
      const variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;
      setButtonLoading(button, true);
      try {
        await cart.add(variantId, { quantity: 1 });
        setButtonLoading(button, false);
        flashSuccess(button);
      } catch (e) {
        setButtonLoading(button, false);
      }
    });

    // Wishlist toggle.
    utils.on(document, 'click', '[data-pwr-wishlist-toggle]', (event, button) => {
      event.preventDefault();
      const productId = button.getAttribute('data-product-id');
      if (!productId) return;
      wishlist.toggle(productId);
    });

    // Initial reflect for server-rendered wishlist buttons.
    wishlist._reflect(document);
  });

  // Re-reflect wishlist buttons when a section (re)loads in the editor.
  bus.on(EVENTS.sectionLoad, (d) => d.target && wishlist._reflect(d.target));

  /* -------------------------------------------------------------- EXPORTS */
  window.PWR.cart = cart;
  window.PWR.wishlist = wishlist;
  window.PWR.Cart = Cart;
  window.PWR.Wishlist = Wishlist;
})();
